import { Controller, useForm } from "react-hook-form"
import { customerFormSchema } from "./customer.schema"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import CustomerAddresses from "./customer-addresses"

interface CustomerFormProps {
  children: React.ReactNode;
  onSubmit: (data: z.infer<typeof customerFormSchema>) => void;
  defaultValues: z.infer<typeof customerFormSchema>;
}

export default function CustomerForm({
  children,
  defaultValues,
  onSubmit,
}: CustomerFormProps) {
  const form = useForm<z.infer<typeof customerFormSchema>>({
    resolver: zodResolver(customerFormSchema),
    defaultValues,
  })

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex-grow flex flex-col"
      >
        <div className="flex flex-col gap-4 py-4 flex-grow">
          <div className="flex flex-row gap-4">
            <FormField
              name="firstName"
              render={({ field }) => (
                <FormItem className="w-6/12">
                  <FormLabel required>First name</FormLabel>
                  <FormControl>
                    <Input placeholder="First name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="lastName"
              render={({ field }) => (
                <FormItem className="w-6/12">
                  <FormLabel required>Last name</FormLabel>
                  <FormControl>
                    <Input placeholder="Last name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            name="organizationName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organization's name</FormLabel>
                <FormControl>
                  <Input placeholder="Organization's name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex flex-row gap-4">
            <FormField
              name="email"
              render={({ field }) => (
                <FormItem className="w-6/12">
                  <FormLabel required>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="Email address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="phoneNumber"
              render={({ field }) => (
                <FormItem className="w-6/12">
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="Phone number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex flex-col gap-4 rounded-md bg-blue-30 p-4">
            <h4 className="text-sm font-semibold">Customer address</h4>
            <Controller
              name="addresses"
              control={form.control}
              render={({ field }) => (
                <CustomerAddresses
                  addresses={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          <FormField
            name="taxId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tax ID</FormLabel>
                <FormControl>
                  <Input placeholder="Tax ID" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {children}
      </form>
    </Form>
  )
}
